<template>
  <CModal
    :show.sync="visible"
    size="lg"
    :title="$gettext('Invite User')"
    @hidden="onHidden"
  >
    <CForm @submit.prevent="onSubmit">
      <CFormGroup>
        <CLabel for="email">
          <translate>Email Address</translate>
        </CLabel>
        <CInput
          id="email"
          v-model="form.email"
          type="email"
          required
          :placeholder="$gettext('Enter email address')"
        />
      </CFormGroup>
      
      <CFormGroup>
        <CLabel>
          <translate>Assign Roles</translate>
        </CLabel>
        <div class="roles-grid">
          <div v-for="role in displayedRoles" :key="role.id" class="role-checkbox-item">
            <div class="d-flex align-items-start">
              <CFormCheck
                :id="`role-${role.id}`"
                v-model="form.roles[role.id]"
                :label="role.name"
              />
              <CButton
                color="link"
                size="sm"
                class="p-0 ml-2"
                @click="showRolePermissions(role)"
                v-c-tooltip="$gettext('View permissions')"
              >
                <CIcon name="cilInfo" size="sm"/>
              </CButton>
            </div>
            <div v-if="form.roles[role.id] && supportsExpiry" class="mt-2 ml-4">
              <CFormCheck
                :id="`role-expires-${role.id}`"
                v-model="form.roleExpiry[role.id].enabled"
                :label="$gettext('Set expiration date')"
                inline
              />
              <CInput
                v-if="form.roleExpiry[role.id].enabled"
                v-model="form.roleExpiry[role.id].date"
                type="date"
                :min="minDate"
                class="mt-2"
                style="max-width: 200px;"
              />
            </div>
          </div>
        </div>
        <div v-if="hasMoreRoles && !showAllRoles" class="text-center mt-3">
          <CButton
            color="link"
            size="sm"
            @click="showAllRoles = true">
            <CIcon name="cilArrowBottom" class="mr-1"/>
            <translate :translate-params="{count: remainingRolesCount}">Show %{count} more roles</translate>
          </CButton>
        </div>
        <div v-if="showAllRoles && hasMoreRoles" class="text-center mt-3">
          <CButton
            color="link"
            size="sm"
            @click="showAllRoles = false">
            <CIcon name="cilArrowTop" class="mr-1"/>
            <translate>Show less</translate>
          </CButton>
        </div>
        <div v-if="!hasSelectedRoles" class="text-danger small mt-2">
          <translate>Please select at least one role</translate>
        </div>
      </CFormGroup>
      
      <CFormGroup>
        <CFormCheck
          id="send-invitation"
          v-model="form.sendInvitation"
          :label="$gettext('Send invitation email')"
        />
        <small class="text-muted ml-4">
          <translate>If unchecked, the user will be added without sending an invitation email</translate>
        </small>
      </CFormGroup>
    </CForm>
    
    <template #footer>
      <CButton color="secondary" @click="visible = false">
        <translate>Cancel</translate>
      </CButton>
      <CButton color="primary" @click="onSubmit" :disabled="!isValid">
        <translate>Send Invitation</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import {mapActions} from 'vuex'
import {sortRolesByPriority} from '../utils/roles'

export default {
  name: 'InviteModal',
  
  props: {
    availableRoles: {
      type: Array,
      default: () => []
    },
    scope: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      visible: false,
      form: {
        email: '',
        roles: {},
        roleExpiry: {},
        sendInvitation: true
      },
      rolesLimit: 20,
      showAllRoles: false
    }
  },
  
  computed: {
    sortedRoles() {
      // Usa la utility per ordinare i ruoli
      // Non abbiamo ruoli assegnati in questo contesto
      return sortRolesByPriority(this.availableRoles, [])
    },
    
    displayedRoles() {
      if (this.showAllRoles) {
        return this.sortedRoles
      }
      return this.sortedRoles.slice(0, this.rolesLimit)
    },
    
    hasMoreRoles() {
      return this.sortedRoles.length > this.rolesLimit
    },
    
    remainingRolesCount() {
      return Math.max(0, this.sortedRoles.length - this.rolesLimit)
    },
    
    hasSelectedRoles() {
      return Object.values(this.form.roles).some(v => v)
    },
    
    isValid() {
      return this.form.email && this.hasSelectedRoles
    },
    
    minDate() {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow.toISOString().split('T')[0]
    },
    
    supportsExpiry() {
      return true
    }
  },
  
  watch: {
    availableRoles: {
      immediate: true,
      handler(roles) {
        roles.forEach(role => {
          this.$set(this.form.roleExpiry, role.id, {
            enabled: false,
            date: ''
          })
        })
      }
    }
  },
  
  methods: {
    ...mapActions('collaborators', [
      'inviteCollaborator',
      'fetchRolePermissions'
    ]),
    
    async onSubmit() {
      if (!this.isValid) return
      
      const roles = []
      Object.keys(this.form.roles).forEach(roleId => {
        if (this.form.roles[roleId]) {
          const roleData = {id: parseInt(roleId)}
          if (this.form.roleExpiry[roleId].enabled && this.form.roleExpiry[roleId].date) {
            roleData.expires_at = new Date(this.form.roleExpiry[roleId].date).toISOString()
          }
          roles.push(roleData)
        }
      })
      
      try {
        await this.inviteCollaborator({
          email: this.form.email,
          roles: roles,
          sendInvitation: this.form.sendInvitation
        })
        
        await this.notifySuccess(this.$pgettext('collaborators.notification', 'Invitation sent successfully'))
        this.close()
        this.$emit('sent')
      } catch (e) {
        await this.notifyError(this.$pgettext('collaborators.notification', 'Failed to send invitation'))
      }
    },
    
    open() {
      this.visible = true
    },
    
    close() {
      this.visible = false
    },
    
    onHidden() {
      this.resetForm()
    },
    
    resetForm() {
      this.form = {
        email: '',
        roles: {},
        roleExpiry: {},
        sendInvitation: true
      }
      this.showAllRoles = false
      
      this.availableRoles.forEach(role => {
        this.$set(this.form.roleExpiry, role.id, {
          enabled: false,
          date: ''
        })
      })
    },
    
    async showRolePermissions(role) {
      // Mostra i permessi del ruolo usando la modale del componente padre
      const permissions = await this.fetchRolePermissions(role.id)
      this.$parent.$refs.rolePermissionsModal.show(permissions, role)
    }
  }
}
</script>

<style scoped>
.roles-grid {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 12px;
}

.role-checkbox-item {
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.role-checkbox-item:last-child {
  border-bottom: none;
}
</style>